/**
 * ============================================================
 * SINGLE SOURCE OF TRUTH FOR ALL ROLES (Admin Panel)
 * ============================================================
 * Keep this in sync with lord-server/config/roles.js
 *
 * Edit ROLES_CONFIG to add/remove/reorder staff roles.
 * Only OWNER (first) and USER (last) are special.
 * ============================================================
 */

const ROLES_CONFIG = [
  { role: 'OWNER',       label: 'Owner' },
  { role: 'COMPANY',     label: 'Company' },
  { role: 'SUPERADMIN',  label: 'SuperAdmin' },
  { role: 'ADMIN',       label: 'Admin' },
  { role: 'DEALER',      label: 'Dealer' },
  { role: 'AGENT',       label: 'Agent' },
  { role: 'USER',        label: 'Bettor' },
];

// ── Derived helpers ──────────────────────────────────────────

/** Role name → hierarchy level number (OWNER=0, USER=last) */
export const HIERARCHY = Object.fromEntries(
  ROLES_CONFIG.map((r, i) => [r.role, i])
);

/** All role names */
export const ALL_ROLES = ROLES_CONFIG.map(r => r.role);

/** Staff roles (not OWNER, not USER) */
export const STAFF_ROLES = ROLES_CONFIG
  .filter(r => r.role !== 'OWNER' && r.role !== 'USER')
  .map(r => r.role);

/** Role options for create-user dropdown (excludes OWNER) */
export const ROLE_OPTIONS = ROLES_CONFIG
  .filter(r => r.role !== 'OWNER')
  .map(r => ({ value: r.role === 'USER' ? 'BETTOR' : r.role, label: r.label }));

/** Check if a role is staff (not OWNER, not USER) */
export const isStaffRole = (role) => {
  const r = role?.toUpperCase();
  return r !== 'OWNER' && r !== 'USER' && r !== 'BETTOR' && ALL_ROLES.includes(r);
};

/** Check if a role is OWNER */
export const isOwnerRole = (role) => role?.toUpperCase() === 'OWNER';

/** Check if a role is USER/BETTOR */
export const isUserRole = (role) => {
  const r = role?.toUpperCase();
  return r === 'USER' || r === 'BETTOR';
};

/** Get level number for a role */
export const getLevelNum = (role) => {
  const r = role?.toUpperCase();
  if (r === 'BETTOR') return HIERARCHY['USER'];
  return HIERARCHY[r] ?? 999;
};

/** Get the correct ID field name for a role */
export const getIdField = (role) => {
  const r = role?.toUpperCase();
  if (r === 'USER' || r === 'BETTOR') return 'user_id';
  if (r === 'OWNER') return 'owner_id';
  return 'staff_id';
};

export default ROLES_CONFIG;
